import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, FileUp, LifeBuoy, Paperclip, Save, X } from "lucide-react";

/* ===================== COMPONENTS ===================== */
import Input from "../../../components/Input/Input";
import Notification from "../../../components/Notification/Notification";

/* ===================== SERVICES ===================== */
import { apiGet, apiPost } from "../../../apiService";
import { getErrorMessage } from "../../../utils/errorHelper";

/* ===================== STYLES ===================== */
import styles from "./RaiseTicket.module.scss";

const BACK_PATH = "/help-desk";
const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024; // 10 MB
const ALLOWED_FILE_TYPES = [
  "image/png",
  "image/jpeg",
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "text/plain",
];
const ALLOWED_EXTENSIONS = [".png", ".jpg", ".jpeg", ".pdf", ".docx", ".txt"];

const RaiseTicket = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [form, setForm] = useState({
    subject: "",
    description: "",
    apiMasterId: "",
    productUrl: "",
  });
  const [file, setFile] = useState(null);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const [productList, setProductList] = useState([]);

  const [notification, setNotification] = useState("");
  const [notificationType, setNotificationType] = useState("success");

  const showNotification = (message, type = "success") => {
    setNotification(message);
    setNotificationType(type);
    setTimeout(() => setNotification(""), 3000);
  };

  const fetchProducts = useCallback(async () => {
    try {
      const res = await apiGet("catalog", "/apimaster/products/dropdown");
      if (res?.status === "success" && Array.isArray(res.data)) {
        setProductList(
          res.data.map((p) => ({
            label: p.name,
            value: String(p.id),
            name: p.name,
          })),
        );
      }
    } catch (err) {
      console.error("Failed to load product list", err);
    }
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validateFile = (picked) => {
    if (!picked) return "";
    if (picked.size > MAX_FILE_SIZE_BYTES) {
      return "File must be 10 MB or smaller";
    }
    const extOk = ALLOWED_EXTENSIONS.some((ext) =>
      picked.name.toLowerCase().endsWith(ext),
    );
    const typeOk = ALLOWED_FILE_TYPES.includes(picked.type);
    if (!extOk && !typeOk) {
      return "Allowed: .png, .jpg, .jpeg, .pdf, .docx, .txt";
    }
    return "";
  };

  const handleFileChange = (e) => {
    const picked = e.target.files?.[0] || null;
    const error = validateFile(picked);
    if (error) {
      setErrors((prev) => ({ ...prev, attachment: error }));
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }
    setErrors((prev) => ({ ...prev, attachment: "" }));
    setFile(picked);
  };

  const handleRemoveFile = () => {
    setFile(null);
    setErrors((prev) => ({ ...prev, attachment: "" }));
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const validate = () => {
    const next = {};
    if (!form.subject.trim()) next.subject = "Subject is required";
    if (!form.description.trim()) next.description = "Description is required";
    if (form.productUrl && form.productUrl.trim()) {
      try {
        // eslint-disable-next-line no-new
        new URL(form.productUrl.trim());
      } catch {
        next.productUrl = "Enter a valid URL (e.g. https://...)";
      }
    }
    return next;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const nextErrors = validate();
    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      return;
    }

    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("subject", form.subject.trim());
      fd.append("description", form.description.trim());
      if (form.apiMasterId) {
        const selected = productList.find(
          (p) => p.value === String(form.apiMasterId),
        );
        fd.append("apiMasterId", String(form.apiMasterId));
        if (selected?.name) {
          fd.append("productName", selected.name);
        }
      }
      if (form.productUrl.trim()) {
        fd.append("productUrl", form.productUrl.trim());
      }
      if (file) {
        fd.append("attachment", file);
      }

      const res = await apiPost("catalog", "/help-desk/tickets", fd);
      if (res?.status === "success") {
        showNotification(
          res?.message || "Ticket raised successfully",
          "success",
        );
        setTimeout(() => navigate(BACK_PATH), 1200);
      } else {
        throw new Error(
          JSON.stringify({
            message: res?.message || "Failed to raise ticket",
          }),
        );
      }
    } catch (err) {
      showNotification(getErrorMessage(err, "Failed to raise ticket"), "error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={styles["raise-ticket__page"]}>
      {notification && (
        <div className={styles["raise-ticket__toast-container"]}>
          <Notification message={notification} type={notificationType} />
        </div>
      )}

      <div className={styles["raise-ticket__header"]}>
        <Link to={BACK_PATH} className={styles["raise-ticket__back-btn"]}>
          <ArrowLeft size={20} strokeWidth={2.5} />
        </Link>
        <div className={styles["raise-ticket__title-group"]}>
          <h2 className={styles["raise-ticket__title"]}>Raise a Ticket</h2>
          <p className={styles["raise-ticket__subtitle"]}>
            Tell us what went wrong — we’ll route it to the right team.
          </p>
        </div>
      </div>

      <form
        onSubmit={handleSubmit}
        noValidate
        className={styles["raise-ticket__form"]}
      >
        <div className={styles["raise-ticket__card"]}>
          <div className={styles["raise-ticket__card-header"]}>
            <div className={styles["raise-ticket__card-icon"]}>
              <LifeBuoy size={18} strokeWidth={2.5} />
            </div>
            <h3 className={styles["raise-ticket__card-title"]}>
              Issue Details
            </h3>
          </div>

          <div className={styles["raise-ticket__grid"]}>
            <Input
              label="Subject"
              name="subject"
              placeholder="Briefly summarize the issue"
              value={form.subject}
              onChange={handleChange}
              error={errors.subject}
              required
              className={styles["raise-ticket__field--full"]}
            />

            <Input
              label="Description"
              type="textarea"
              name="description"
              placeholder="Describe what happened, including steps to reproduce"
              value={form.description}
              onChange={handleChange}
              error={errors.description}
              required
              className={styles["raise-ticket__field--full"]}
            />

            <Input
              label="Product Name"
              type="select"
              name="apiMasterId"
              placeholder="Select a product"
              value={form.apiMasterId}
              onChange={handleChange}
              error={errors.apiMasterId}
              options={[{ label: "None", value: "" }, ...productList]}
            />

            <Input
              label="Product URL"
              name="productUrl"
              placeholder="https://product.doql.com/path"
              value={form.productUrl}
              onChange={handleChange}
              error={errors.productUrl}
              className={styles["raise-ticket__field--full"]}
            />
          </div>
        </div>

        {/* ATTACHMENT */}
        <div className={styles["raise-ticket__card"]}>
          <div className={styles["raise-ticket__card-header"]}>
            <div className={styles["raise-ticket__card-icon"]}>
              <Paperclip size={18} strokeWidth={2.5} />
            </div>
            <h3 className={styles["raise-ticket__card-title"]}>Attachment</h3>
          </div>

          <p className={styles["raise-ticket__hint-text"]}>
            Optional. Allowed: .png, .jpg, .jpeg, .pdf, .docx, .txt — up to 10
            MB.
          </p>

          <input
            ref={fileInputRef}
            type="file"
            accept=".png,.jpg,.jpeg,.pdf,.docx,.txt"
            onChange={handleFileChange}
            className={styles["raise-ticket__file-hidden"]}
            id="raise-ticket-file"
          />

          {!file ? (
            <label
              htmlFor="raise-ticket-file"
              className={styles["raise-ticket__dropzone"]}
            >
              <FileUp size={22} strokeWidth={2.5} />
              <span>Click to choose a file</span>
              <small>PNG · JPG · PDF · DOCX · TXT</small>
            </label>
          ) : (
            <div className={styles["raise-ticket__file-chip"]}>
              <Paperclip size={16} strokeWidth={2.5} />
              <div className={styles["raise-ticket__file-meta"]}>
                <strong>{file.name}</strong>
                <span>{(file.size / (1024 * 1024)).toFixed(2)} MB</span>
              </div>
              <button
                type="button"
                className={styles["raise-ticket__file-remove"]}
                onClick={handleRemoveFile}
                aria-label="Remove attachment"
              >
                <X size={16} strokeWidth={2.5} />
              </button>
            </div>
          )}

          {errors.attachment && (
            <p className={styles["raise-ticket__error-text"]}>
              {errors.attachment}
            </p>
          )}
        </div>

        <div className={styles["raise-ticket__actions"]}>
          <button
            type="button"
            className={`${styles["raise-ticket__btn"]} ${styles["raise-ticket__btn--secondary"]}`}
            onClick={() => navigate(BACK_PATH)}
            disabled={submitting}
          >
            <X size={16} strokeWidth={2.5} />
            Cancel
          </button>

          <button
            type="submit"
            className={`${styles["raise-ticket__btn"]} ${styles["raise-ticket__btn--primary"]}`}
            disabled={submitting}
          >
            <Save size={16} strokeWidth={2.5} />
            {submitting ? "Submitting…" : "Submit Ticket"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default RaiseTicket;
